import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, ArrowRight, ArrowLeft, Check, User, Building, Briefcase, HelpCircle, Mail, Upload, FileText, Loader2, Sparkles } from 'lucide-react';

const CreerCoproprieteV2 = ({ user, onUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [etape, setEtape] = useState(0); // 0: Type créateur, 1: Infos, 2: Postes, 3: Copros, 4: Attribution, 5: Récapitulatif

  // Composant de contact réutilisable
  const ContactInfo = () => (
    <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
      <p className="text-sm text-indigo-700 flex items-center gap-2">
        <Mail className="w-4 h-4 flex-shrink-0" />
        <span>
          <strong>Une question ?</strong> N'hésitez pas à nous contacter à{' '}
          <a href="mailto:gmpdeveloppement@gmail.com" className="underline font-semibold hover:text-indigo-900">
            gmpdeveloppement@gmail.com
          </a>
        </span>
      </p>
    </div>
  );

  // Étape 5 : Récapitulatif final
  const [createdCredentials, setCreatedCredentials] = useState([]);
  const [creationComplete, setCreationComplete] = useState(false);

  // Étape 0 : Type de créateur
  const [createurType, setCreateurType] = useState(null); // 'membre' | 'syndic_pro'
  const [syndicProInfo, setSyndicProInfo] = useState({
    siret: '',
    raisonSociale: ''
  });

  // Étape 1 : Infos générales
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
  });

  // Étape 2 : Postes de dépenses
  const [postesDepenses, setPostesDepenses] = useState([
    { id: Date.now().toString(), nom: 'Charges générales', description: '' }
  ]);

  // Étape 3 : Copropriétaires
  const [coproprietaires, setCoproprietaires] = useState([]);
  
  // Import EDD PDF
  const [extractingPdf, setExtractingPdf] = useState(false);
  const [extractedLots, setExtractedLots] = useState(null);
  const fileInputRef = useRef(null);

  // Étape 4 : Attribution (poste -> copro -> tantièmes)
  const [attributions, setAttributions] = useState({});

  // Initialiser les copropriétaires en fonction du choix du créateur
  const initCoproprietaires = (type) => {
    if (type === 'membre') {
      // Préremplir avec les infos de l'utilisateur connecté
      setCoproprietaires([
        { 
          id: Date.now().toString(), 
          prenom: user.prenom || '', 
          nom: user.nom || '', 
          email: user.email || '', 
          telephone: '', 
          lot: '',
          role: 'président' // Par défaut président s'il crée la copro
        }
      ]);
    } else {
      // Syndic pro - liste vide, il ajoutera les copropriétaires
      setCoproprietaires([
        { id: Date.now().toString(), prenom: '', nom: '', email: '', telephone: '', lot: '', role: 'copropriétaire' }
      ]);
    }
  };

  const addPoste = () => {
    setPostesDepenses([...postesDepenses, { 
      id: Date.now().toString(), 
      nom: '', 
      description: '' 
    }]);
  };

  const removePoste = (id) => {
    setPostesDepenses(postesDepenses.filter(p => p.id !== id));
  };

  const updatePoste = (id, field, value) => {
    setPostesDepenses(postesDepenses.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const addCopro = () => {
    setCoproprietaires([...coproprietaires, { 
      id: Date.now().toString(), 
      prenom: '', 
      nom: '', 
      email: '', 
      telephone: '', 
      lot: '',
      role: 'copropriétaire'
    }]);
  };

  const removeCopro = (id) => {
    setCoproprietaires(coproprietaires.filter(c => c.id !== id));
  };

  const updateCopro = (id, field, value) => {
    setCoproprietaires(coproprietaires.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  // Fonction d'extraction PDF EDD
  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }

    // Limite à 10 Mo
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 10 Mo). Essayez de compresser le PDF.');
      return;
    }

    setExtractingPdf(true);
    const toastId = toast.loading('Analyse du document en cours... Cela peut prendre jusqu\'à 1 minute.');

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await apiClient.post('/extract-edd', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // 3 minutes pour l'analyse
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (percentCompleted === 100) {
            toast.loading('Upload terminé, analyse IA en cours...', { id: toastId });
          }
        }
      });

      toast.dismiss(toastId);
      const data = response.data;
      
      if (data.success && data.lots?.length > 0) {
        setExtractedLots(data);
        
        // Pré-remplir les infos de la copropriété si disponibles
        if (data.nom_copropriete) {
          setFormData(prev => ({ ...prev, nom: data.nom_copropriete }));
        }
        if (data.adresse) {
          setFormData(prev => ({ ...prev, adresse: data.adresse }));
        }
        
        // Convertir les lots extraits en copropriétaires
        const newCopros = data.lots.map((lot, index) => ({
          id: lot.id || Date.now().toString() + index,
          prenom: '',
          nom: lot.proprietaire || `Propriétaire Lot ${lot.numero_lot}`,
          email: '',
          telephone: '',
          lot: `${lot.numero_lot}${lot.description ? ` - ${lot.description}` : ''}${lot.etage ? ` (${lot.etage})` : ''}`,
          role: index === 0 ? 'président' : 'copropriétaire',
          tantiemes_from_edd: lot.tantiemes || 0
        }));

        // Garder le premier copropriétaire si c'est le créateur membre
        if (createurType === 'membre' && coproprietaires.length > 0) {
          const createur = coproprietaires[0];
          newCopros.unshift({
            ...createur,
            tantiemes_from_edd: createur.tantiemes_from_edd || 0
          });
        }

        setCoproprietaires(newCopros);
        toast.success(`${data.lots.length} lots extraits ! Les informations ont été pré-remplies.`);
      } else {
        toast.error('Aucun lot trouvé dans le document');
      }
    } catch (error) {
      console.error('Erreur extraction PDF:', error);
      toast.dismiss();
      const errorMsg = error.response?.data?.detail || getErrorMessage(error) || 'Erreur lors de l\'analyse du PDF';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('L\'analyse a pris trop de temps. Essayez avec un PDF plus petit ou moins complexe.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setExtractingPdf(false);
      // Reset l'input file pour permettre de re-sélectionner le même fichier
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const togglePosteForCopro = (posteId, coproId) => {
    setAttributions(prev => {
      const newAttr = JSON.parse(JSON.stringify(prev));
      if (!newAttr[posteId]) newAttr[posteId] = {};
      
      if (newAttr[posteId][coproId] !== undefined) {
        delete newAttr[posteId][coproId];
      } else {
        newAttr[posteId][coproId] = 0;
      }
      return newAttr;
    });
  };

  const updateTantiemes = (posteId, coproId, value) => {
    setAttributions(prev => ({
      ...prev,
      [posteId]: {
        ...prev[posteId],
        [coproId]: parseFloat(value) || 0
      }
    }));
  };

  const getTotalTantiemesForPoste = (posteId) => {
    const posteAttr = attributions[posteId] || {};
    return Object.values(posteAttr).reduce((sum, val) => sum + val, 0);
  };

  const validateEtape0 = () => {
    if (!createurType) {
      toast.error('Veuillez sélectionner votre situation');
      return false;
    }
    if (createurType === 'syndic_pro' && !syndicProInfo.siret) {
      toast.error('Veuillez renseigner le SIRET');
      return false;
    }
    return true;
  };

  const validateEtape1 = () => {
    if (!formData.nom || !formData.adresse) {
      toast.error('Veuillez remplir le nom et l\'adresse');
      return false;
    }
    return true;
  };

  const validateEtape2 = () => {
    if (postesDepenses.length === 0) {
      toast.error('Ajoutez au moins un poste de dépense');
      return false;
    }
    if (postesDepenses.some(p => !p.nom.trim())) {
      toast.error('Tous les postes doivent avoir un nom');
      return false;
    }
    return true;
  };

  const validateEtape3 = () => {
    if (coproprietaires.length === 0) {
      toast.error('Ajoutez au moins un copropriétaire');
      return false;
    }
    if (coproprietaires.some(c => !c.lot || !c.nom)) {
      toast.error('Veuillez remplir le N° de lot et le nom pour chaque copropriétaire');
      return false;
    }
    // Vérifier que le créateur (premier) a bien un email
    if (createurType === 'membre' && !coproprietaires[0]?.email) {
      toast.error('Votre email est obligatoire');
      return false;
    }
    return true;
  };

  const validateEtape4 = () => {
    for (const poste of postesDepenses) {
      const total = getTotalTantiemesForPoste(poste.id);
      if (Math.abs(total - 1000) > 0.01) {
        toast.error(`Le poste "${poste.nom}" doit avoir exactement 1000 tantièmes (actuellement: ${total.toFixed(2)})`);
        return false;
      }
    }
    return true;
  };

  const nextEtape = () => {
    if (etape === 0) {
      if (!validateEtape0()) return;
      initCoproprietaires(createurType);
    }
    if (etape === 1 && !validateEtape1()) return;
    if (etape === 2 && !validateEtape2()) return;
    if (etape === 3 && !validateEtape3()) return;
    
    if (etape === 3) {
      // Initialiser les attributions avec les tantièmes de l'EDD si disponibles
      const newAttr = {};
      postesDepenses.forEach(poste => {
        newAttr[poste.id] = {};
        // Pré-remplir avec les tantièmes extraits de l'EDD
        coproprietaires.forEach(copro => {
          if (copro.tantiemes_from_edd > 0) {
            newAttr[poste.id][copro.id] = copro.tantiemes_from_edd;
          }
        });
      });
      setAttributions(newAttr);
    }
    
    setEtape(etape + 1);
  };

  const prevEtape = () => {
    setEtape(etape - 1);
  };

  const handleSubmit = async () => {
    if (!validateEtape4()) return;

    setLoading(true);

    try {
      const coproData = coproprietaires.map(c => ({
        temp_id: c.id,
        prenom: c.prenom,
        nom: c.nom,
        email: c.email,
        telephone: c.telephone,
        lot: c.lot,
        role: createurType === 'syndic_pro' && c.email === user.email ? 'syndic_pro' : c.role
      }));

      const postesData = postesDepenses.map(p => ({
        nom: p.nom,
        description: p.description,
        repartition: attributions[p.id] || {}
      }));

      // Ajouter les infos syndic pro si applicable
      const requestData = {
        nom: formData.nom,
        adresse: formData.adresse,
        coproprietaires: coproData,
        postes_depenses: postesData
      };

      if (createurType === 'syndic_pro') {
        requestData.syndic_pro = {
          siret: syndicProInfo.siret,
          raison_sociale: syndicProInfo.raisonSociale
        };
      }

      const response = await apiClient.post('/coproprietes', requestData);

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      const userResponse = await apiClient.get('/auth/me');
      onUpdate(userResponse.data);
      localStorage.setItem('user', JSON.stringify(userResponse.data));

      // Stocker les credentials créés et passer à l'étape récapitulatif
      setCreatedCredentials(response.data.created_users || []);
      setCreationComplete(true);
      setEtape(5);
      
      toast.success('Copropriété créée avec succès !');
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la création');
      if (message) toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const etapeLabels = ['Votre rôle', 'Infos', 'Postes', 'Copros', 'Attribution', 'Terminé'];

  return (
    <div className="min-h-screen bg-slate-50 py-4 px-4 md:py-8 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {[0, 1, 2, 3, 4, 5].map(step => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                  step < etape ? 'bg-green-500 text-white' :
                  step === etape ? 'bg-indigo-600 text-white' :
                  'bg-slate-200 text-slate-500'
                }`}>
                  {step < etape ? <Check className="w-4 h-4 md:w-5 md:h-5" /> : step + 1}
                </div>
                {step < 5 && <div className={`w-6 md:w-16 h-1 mx-1 md:mx-2 ${step < etape ? 'bg-green-500' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-600">
            {etapeLabels.map((label, i) => (
              <span key={i} className="text-center">{label}</span>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          {/* Étape 0 : Type de créateur */}
          {etape === 0 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Bienvenue !</h2>
              <p className="text-slate-600 mb-6">Avant de créer votre copropriété, dites-nous en plus sur vous.</p>
              
              <div className="mb-6">
                <Label className="text-lg font-medium mb-4 block">Faites-vous partie de cette copropriété ?</Label>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Option : Membre de la copro */}
                  <button
                    type="button"
                    onClick={() => setCreateurType('membre')}
                    className={`p-6 rounded-xl border-2 transition-all text-left ${
                      createurType === 'membre'
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        createurType === 'membre' ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <User className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Oui, j'en fais partie</h3>
                        <p className="text-sm text-slate-500">Copropriétaire, président, etc.</p>
                      </div>
                    </div>
                    {createurType === 'membre' && (
                      <div className="mt-4 p-3 bg-indigo-100 rounded-lg">
                        <p className="text-sm text-indigo-700">
                          Vos informations ({user.email}) seront automatiquement ajoutées à la liste des copropriétaires.
                        </p>
                      </div>
                    )}
                  </button>

                  {/* Option : Syndic professionnel */}
                  <button
                    type="button"
                    onClick={() => setCreateurType('syndic_pro')}
                    className={`p-6 rounded-xl border-2 transition-all text-left ${
                      createurType === 'syndic_pro'
                        ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        createurType === 'syndic_pro' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <Briefcase className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Non, je suis syndic professionnel</h3>
                        <p className="text-sm text-slate-500">Je gère pour le compte de clients</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Formulaire syndic pro */}
              {createurType === 'syndic_pro' && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                  <h4 className="font-semibold text-amber-900">Informations du syndic professionnel</h4>
                  <div>
                    <Label>Numéro SIRET *</Label>
                    <Input
                      value={syndicProInfo.siret}
                      onChange={(e) => setSyndicProInfo({ ...syndicProInfo, siret: e.target.value })}
                      placeholder="Ex: 123 456 789 00012"
                      maxLength={17}
                    />
                  </div>
                  <div>
                    <Label>Raison sociale (optionnel)</Label>
                    <Input
                      value={syndicProInfo.raisonSociale}
                      onChange={(e) => setSyndicProInfo({ ...syndicProInfo, raisonSociale: e.target.value })}
                      placeholder="Ex: Cabinet Martin Syndic"
                    />
                  </div>
                </div>
              )}
              
              <ContactInfo />
            </div>
          )}

          {/* Étape 1 : Informations */}
          {etape === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Informations de la copropriété</h2>
              
              {/* Section Import PDF EDD */}
              <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-emerald-900 mb-1">
                      Import automatique depuis l'EDD
                    </h3>
                    <p className="text-sm text-emerald-700 mb-3">
                      Importez votre État Descriptif de Division (EDD) en PDF et l'IA extraira automatiquement le nom, l'adresse, les lots, tantièmes et propriétaires.
                    </p>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                      id="edd-pdf-upload"
                    />
                    
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={extractingPdf}
                      variant="outline"
                      className="bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      {extractingPdf ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyse en cours...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Importer un PDF (EDD)
                        </>
                      )}
                    </Button>
                    
                    {extractedLots && (
                      <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {extractedLots.lots.length} lots extraits - Les informations ont été pré-remplies
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Nom de la copropriété *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Ex: Résidence Les Mimosas"
                  />
                </div>
                <div>
                  <Label>Adresse *</Label>
                  <Input
                    value={formData.adresse}
                    onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    placeholder="Ex: 12 rue Victor Hugo, 75001 Paris"
                  />
                </div>
              </div>
              
              <ContactInfo />
            </div>
          )}

          {/* Étape 2 : Postes de dépenses */}
          {etape === 2 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Postes de dépenses</h2>
                <Button onClick={addPoste} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter
                </Button>
              </div>
              <p className="text-slate-600 mb-4">
                Les postes permettent de répartir les charges (ex: Charges générales, Ascenseur, Chauffage...).
              </p>
              <div className="space-y-3">
                {postesDepenses.map((poste, idx) => (
                  <div key={poste.id} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                    <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-medium">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <Input
                        value={poste.nom}
                        onChange={(e) => updatePoste(poste.id, 'nom', e.target.value)}
                        placeholder="Nom du poste"
                      />
                    </div>
                    {postesDepenses.length > 1 && (
                      <Button onClick={() => removePoste(poste.id)} size="sm" variant="ghost" className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <ContactInfo />
            </div>
          )}

          {/* Étape 3 : Copropriétaires */}
          {etape === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Copropriétaires</h2>
              
              {/* Message si des lots ont été extraits de l'EDD */}
              {extractedLots && extractedLots.lots.length > 0 && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-700 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span><strong>{extractedLots.lots.length} lots</strong> pré-remplis depuis l'EDD. Vérifiez et complétez les informations.</span>
                  </p>
                </div>
              )}
              
              {createurType === 'membre' && (
                <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-sm text-indigo-700">
                    Le premier copropriétaire correspond à vos informations. Vous pouvez modifier le rôle si nécessaire.
                  </p>
                </div>
              )}

              <div className="space-y-4">
                {coproprietaires.map((copro, idx) => (
                  <div key={copro.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-slate-700">Copropriétaire {idx + 1}</span>
                      {coproprietaires.length > 1 && (
                        <Button onClick={() => removeCopro(copro.id)} size="sm" variant="ghost" className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        value={copro.lot}
                        onChange={(e) => updateCopro(copro.id, 'lot', e.target.value)}
                        placeholder="N° Lot *"
                      />
                      <Input
                        value={copro.nom}
                        onChange={(e) => updateCopro(copro.id, 'nom', e.target.value)}
                        placeholder="Nom du propriétaire *"
                      />
                      <Input
                        value={copro.email}
                        onChange={(e) => updateCopro(copro.id, 'email', e.target.value)}
                        placeholder={idx === 0 && createurType === 'membre' ? "Email *" : "Email (optionnel)"}
                        type="email"
                      />
                      <Input
                        value={copro.telephone}
                        onChange={(e) => updateCopro(copro.id, 'telephone', e.target.value)}
                        placeholder="Téléphone (optionnel)"
                      />
                      <select
                        value={copro.role}
                        onChange={(e) => updateCopro(copro.id, 'role', e.target.value)}
                        className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      >
                        <option value="copropriétaire">Copropriétaire</option>
                        <option value="président">Président</option>
                        <option value="trésorier">Trésorier</option>
                        <option value="secrétaire">Secrétaire</option>
                      </select>
                    </div>
                    {/* Afficher les tantièmes extraits de l'EDD */}
                    {copro.tantiemes_from_edd > 0 && (
                      <div className="mt-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg inline-flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-sm text-emerald-700">
                          Tantièmes EDD : <strong>{copro.tantiemes_from_edd}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Bouton Ajouter en bas pour bien voir les copropriétaires ajoutés */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <Button onClick={addCopro} variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un copropriétaire
                </Button>
              </div>
              
              <ContactInfo />
            </div>
          )}

          {/* Étape 4 : Attribution des tantièmes */}
          {etape === 4 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Attribution des tantièmes</h2>
              
              {/* Encadré d'aide pour trouver les tantièmes */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-2">Où trouver les tantièmes ?</h4>
                    <p className="text-sm text-amber-800 mb-2">
                      Les tantièmes de chaque copropriétaire se trouvent dans les documents officiels de votre copropriété :
                    </p>
                    <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
                      <li><strong>L'État Descriptif de Division (EDD)</strong> : document notarié qui définit les lots et leurs tantièmes</li>
                      <li><strong>Les PV d'Assemblées Générales</strong> : ils reprennent souvent le tableau des tantièmes</li>
                      <li><strong>Les anciens appels de fonds</strong> : ils mentionnent les tantièmes de chaque copropriétaire</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6">
                Pour chaque poste, sélectionnez les copropriétaires concernés et attribuez les tantièmes.
                <br />
                <span className="font-medium">Le total doit être de 1000 tantièmes par poste.</span>
              </p>

              <div className="space-y-6">
                {postesDepenses.map(poste => {
                  const total = getTotalTantiemesForPoste(poste.id);
                  const isComplete = Math.abs(total - 1000) < 0.01;
                  
                  return (
                    <div key={poste.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className={`p-4 flex items-center justify-between ${isComplete ? 'bg-green-50' : 'bg-slate-50'}`}>
                        <h3 className="font-semibold text-slate-900">{poste.nom}</h3>
                        <span className={`text-sm font-medium ${isComplete ? 'text-green-600' : 'text-red-600'}`}>
                          {total.toFixed(0)} / 1000
                        </span>
                      </div>
                      <div className="p-4 space-y-3">
                        {coproprietaires.map(copro => {
                          const isSelected = attributions[poste.id]?.[copro.id] !== undefined;
                          return (
                            <div key={copro.id} className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePosteForCopro(poste.id, copro.id)}
                                className="w-5 h-5 rounded"
                              />
                              <span className="flex-1 text-sm">{copro.nom} (Lot {copro.lot})</span>
                              {isSelected && (
                                <Input
                                  type="number"
                                  value={attributions[poste.id]?.[copro.id] || 0}
                                  onChange={(e) => updateTantiemes(poste.id, copro.id, e.target.value)}
                                  className="w-24"
                                  placeholder="Tantièmes"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <ContactInfo />
            </div>
          )}

          {/* Étape 5 : Récapitulatif final */}
          {etape === 5 && creationComplete && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Copropriété créée avec succès !</h2>
                <p className="text-slate-600">Votre copropriété "{formData.nom}" est maintenant prête.</p>
              </div>

              {createdCredentials.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Mots de passe provisoires à transmettre
                  </h3>
                  <p className="text-sm text-amber-700 mb-4">
                    Transmettez ces informations de connexion aux copropriétaires. 
                    Ils devront changer leur mot de passe à la première connexion.
                  </p>
                  <div className="bg-white rounded-lg overflow-hidden border border-amber-200">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-100">
                        <tr>
                          <th className="text-left py-2 px-4 font-medium">Nom</th>
                          <th className="text-left py-2 px-4 font-medium">Email</th>
                          <th className="text-left py-2 px-4 font-medium">Mot de passe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {createdCredentials.map((cred, idx) => (
                          <tr key={idx} className="border-t border-amber-100">
                            <td className="py-2 px-4">{cred.nom} (Lot {cred.lot})</td>
                            <td className="py-2 px-4 font-mono text-xs">{cred.email}</td>
                            <td className="py-2 px-4 font-mono text-xs bg-amber-50 font-semibold">{cred.password}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={() => {
                      const text = createdCredentials.map(c => 
                        `${c.nom} (Lot ${c.lot})\nEmail: ${c.email}\nMot de passe: ${c.password}\n`
                      ).join('\n---\n');
                      navigator.clipboard.writeText(text);
                      toast.success('Copié dans le presse-papier !');
                    }}
                    className="mt-4 text-sm text-amber-700 hover:text-amber-900 underline"
                  >
                    📋 Copier tous les accès
                  </button>
                </div>
              )}

              {/* Message de contact */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-indigo-700">
                  <strong>Une question ?</strong> N'hésitez pas à nous contacter à{' '}
                  <a href="mailto:gmpdeveloppement@gmail.com" className="underline font-semibold">
                    gmpdeveloppement@gmail.com
                  </a>
                </p>
              </div>

              <Button onClick={() => navigate('/')} className="w-full bg-indigo-600 hover:bg-indigo-700">
                Accéder au tableau de bord
              </Button>
            </div>
          )}

          {/* Navigation */}
          {etape < 5 && (
            <div className="flex justify-between mt-8 pt-6 border-t">
              {etape > 0 && !creationComplete ? (
                <Button onClick={prevEtape} variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Précédent
                </Button>
              ) : (
                <div></div>
              )}
              
              {etape < 4 ? (
                <Button onClick={nextEtape} disabled={etape === 0 && !createurType}>
                  Suivant <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : etape === 4 ? (
                <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
                  {loading ? 'Création...' : 'Créer la copropriété'}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreerCoproprieteV2;
